import api from '@/services/axios/api';

/**
 * Código da etapa "Nota Final" (NF).
 *
 * É o mesmo `RegraEtapaNotaFinal.Codigo` do backend. A NF não é uma etapa de lançamento: é o
 * fechamento do ano, e só aparece no combo quando a etapa atual já alcançou o número de etapas do
 * ensino.
 */
export const ETAPA_NOTA_FINAL = '11';

/**
 * A etapa que se manda ao cálculo quando a emissão é do RESULTADO FINAL.
 *
 * O backend decide se o resultado é definitivo por `Etapa > NumeroEtapas` — e nenhum ensino tem
 * mais de cinco etapas, então onze sempre significa "o ano fechou". É o mesmo número da etapa NF,
 * de propósito: são a mesma coisa vista de dois lados.
 */
export const ETAPA_RESULTADO_FINAL = 11;

/**
 * Os estados de uma turma dentro do trabalho — espelham `EstadoDaTurmaNoTrabalho`, no servidor.
 *
 * `Ignorada` não é falha: é a turma que não tinha o que recalcular (fechada, sem matrícula, sem
 * grade). Ela conta como resolvida, e não deve segurar a impressão.
 */
export const ESTADO_TURMA = {
  naFila: 0,
  calculando: 1,
  calculada: 2,
  comErro: 3,
  ignorada: 4,
} as const;

/** Uma turma dentro de um trabalho de recálculo. */
export class TurmaDoTrabalhoModel {
  codigoTurma = 0;
  turma: string | null = null;

  /** 0 na fila, 1 calculando, 2 calculada, 3 com erro, 4 ignorada. */
  estado = 0;

  avaliadas = 0;
  alteradas = 0;
  linhasDaAtaFechamento = 0;
  motivo: string | null = null;
}

/** O andamento de um trabalho de recálculo. */
export class TrabalhoSituacaoFinalModel {
  id = '';
  codigoEscola = 0;
  ano = 0;
  semestre = 0;
  etapa = 0;
  estado = '';
  solicitante: string | null = null;
  criadoEm = '';
  concluidoEm: string | null = null;

  total = 0;
  concluidas = 0;
  turmaAtual: number | null = null;
  nomeDaTurmaAtual: string | null = null;
  terminou = false;
  alteradas = 0;

  turmas: TurmaDoTrabalhoModel[] = [];
  comErro: TurmaDoTrabalhoModel[] = [];
}

/** O que a fila responde a quem enfileira. */
export class EnfileiramentoModel {
  /** Nulo quando nenhuma turma entrou — todas já estavam em cálculo. */
  idTrabalho: string | null = null;

  enfileiradas: number[] = [];

  /** As turmas recusadas porque já estão em cálculo, possivelmente por outro usuário. */
  jaEmCalculo: number[] = [];

  trabalhosEmAndamento: string[] = [];
}

/** O resultado de um recálculo síncrono. */
export class RecalculoModel {
  codigoTurma = 0;
  turma: string | null = null;
  avaliadas = 0;
  alteradas = 0;
  calculou = false;
  motivo: string | null = null;
  linhasDaAtaFechamento = 0;
}

/**
 * O cálculo da SITUAÇÃO FINAL do estudante.
 *
 * Existem dois caminhos, e a diferença é de propósito:
 *
 * - **Síncrono** (`recalcular`): uma turma, ou um estudante dela. É o que boletim, ficha e
 *   "Visualizar Totais" usam quando a etapa escolhida é a NF — o relatório sai logo depois e tem
 *   de mostrar o resultado novo, então esperar é o certo.
 * - **Assíncrono** (`calcular` + `acompanhar`): as turmas de uma escola inteira. É o que a ATA
 *   usa. Quarenta turmas não cabem numa requisição, e travar a tela do usuário enquanto rodam
 *   seria pior do que mostrar o andamento.
 *
 * A mesma turma nunca é recalculada duas vezes ao mesmo tempo — a fila do servidor cuida disso, e
 * devolve em `jaEmCalculo` o que recusou.
 */
class SituacaoFinalService {
  /**
   * Recalcula e GRAVA, esperando o resultado.
   *
   * @param codigoAluno Zero recalcula a turma inteira. Com um estudante escolhido, recalcula só
   *                    ele — não faz sentido recalcular quarenta matrículas para imprimir uma.
   */
  async recalcular(
    codigoTurma: number,
    etapa: number,
    codigoAluno = 0,
  ): Promise<RecalculoModel> {
    const { data } = await api.post('/SituacaoFinal/Recalcular', {
      codigoTurma,
      etapa,
      codigoAluno,
    });

    return data;
  }

  /** Enfileira o recálculo das turmas de uma escola. Devolve o número do trabalho. */
  async calcular(pedido: {
    codigoEscola: number;
    ano: number;
    semestre: number;
    etapa: number;
    turmas: number[];
    repovoarAtaFechamento?: boolean;
    /**
     * O relatório a emitir quando o cálculo terminar, guardado na TAREFA.
     *
     * É o que permite reemitir a ata no mesmo dia — de outra máquina, depois de um F5, ou dias
     * depois — sem recalcular: a janela "Minhas Tarefas" oferece o botão de emitir com estes
     * filtros. Os mesmos filtros já viviam no `localStorage` da caixa de acompanhamento, mas ali
     * morrem com o navegador.
     *
     * Quem não emite relatório (boletim e ficha, que recalculam de forma síncrona) simplesmente
     * não manda.
     */
    codigoRelatorio?: string;
    filtrosRelatorio?: string | null;
    tituloRelatorio?: string;
  }): Promise<EnfileiramentoModel> {
    const { data } = await api.post('/SituacaoFinal/Calcular', {
      repovoarAtaFechamento: true,
      ...pedido,
    });

    return data;
  }

  /** O andamento de um trabalho. */
  async acompanhar(idTrabalho: string): Promise<TrabalhoSituacaoFinalModel> {
    const { data } = await api.get(`/SituacaoFinal/Trabalho/${idTrabalho}`);

    return data;
  }

  /** Os trabalhos de uma escola — para saber se já há um recálculo em andamento. */
  async trabalhosDaEscola(codigoEscola: number): Promise<TrabalhoSituacaoFinalModel[]> {
    const { data } = await api.get('/SituacaoFinal/Trabalhos', {
      params: { codigoEscola },
    });

    return data;
  }
}

export default new SituacaoFinalService();
